/**
 * Deterministic, dependency-free stand-ins for the small slice of test-data generation the
 * mocks and tests previously pulled from `@faker-js/faker`.
 *
 * The real dependency was removed to clear CVE-2026-73231 (CWE-95 Eval Injection in
 * `faker.helpers.fake`). faker was dev-only and its vulnerable method was never used here; it
 * only generated fixture data. These helpers produce stable, unique-enough values for test
 * fixtures — a module-scoped counter keeps ids/paths distinct within a single test run, without
 * the randomness (and flakiness) that faker introduced.
 */
let seq = 0;
const nextSeq = (): number => (seq += 1);

/** Space-joined placeholder words; `count` controls how many (default 2). */
const words = (count = 2): string =>
  Array.from({ length: Math.max(1, count) }, (_, i) => `word${i + 1}`).join(' ');

export const fake = {
  /** RFC-4122-shaped, unique per call within a test run. */
  uuid: (): string =>
    `00000000-0000-4000-8000-${nextSeq().toString(16).padStart(12, '0')}`,

  /**
   * Non-negative integer. Mirrors the faker.datatype.number signatures used here:
   * a bare max (`number(10)`) or an options object (`number({ min: 1 })`).
   */
  number: (arg?: number | { min?: number; max?: number }): number => {
    if (typeof arg === 'number') return nextSeq() % (arg + 1);
    const min = arg?.min ?? 0;
    return min + (nextSeq() % 1000);
  },

  word: (): string => `word${nextSeq()}`,
  words,
  sentence: (): string => `Sample sentence number ${nextSeq()}.`,
  fullName: (): string => `Test User ${nextSeq()}`,
  noun: (): string => `noun${nextSeq()}`,
  email: (): string => `user${nextSeq()}@example.com`,
  url: (): string => `https://example.com/${nextSeq()}`,
  accountName: (): string => `Account ${nextSeq()}`,
  fileName: (): string => `file-${nextSeq()}.txt`,
  /** Absolute path with a folder segment and a file, matching faker.system.filePath() shape. */
  filePath: (): string => `/folder-${nextSeq()}/file-${nextSeq()}.txt`,
  directoryPath: (): string => `/folder-${nextSeq()}`,
  pastDate: (): Date => new Date('2020-06-15T10:00:00.000Z'),
  recentDate: (): Date => new Date('2026-06-15T10:00:00.000Z'),
};
